import {
  OrderTrackingShipmentStatus,
  TRACKER_STATUS_TO_SHIPMENT_STATUS,
} from "@redotech/order-tracking-common/order-tracking-types";
import type {
  TrackingDetailsExtraStatusKey,
  TrackingDetailsStatusLabelKey,
} from "@redotech/order-tracking-common/tracking-page-definition";
import type {
  TrackingPageEvent,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import {
  applyTrackingLocationRules,
  applyTrackingTextRules,
} from "@redotech/order-tracking-common/tracking-text-rules";
import { t } from "@redotech/redo-i18n";
import { getCarrierDisplayName } from "@redotech/redo-order-tracking-settings-common/carriers/carrier-resolver";
import type { TrackingTextRule } from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { createElement, memo } from "react";
import { getCarrierIcon } from "../../../carrier/get-carrier-icon";
import type { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";

export type StatusLabelOverrides = Partial<
  Record<TrackingDetailsStatusLabelKey, string>
>;

// Merchant override when present and non-blank, else the built-in fallback.
// Empty/whitespace falls back so a cleared input never renders blank.
function resolveLabel(
  status: TrackingDetailsStatusLabelKey,
  overrides: StatusLabelOverrides | undefined,
  fallback: string,
): string {
  return overrides?.[status]?.trim() || fallback;
}

// Computed lazily so the localized copy is read at call time, and exported so
// the builder inspector can use each built-in label as its input placeholder.
export function statusStrings(): Record<OrderTrackingShipmentStatus, string> {
  return {
    [OrderTrackingShipmentStatus.PRE_TRANSIT]: t("Pre-transit"),
    [OrderTrackingShipmentStatus.PENDING]: t("Pending"),
    [OrderTrackingShipmentStatus.IN_TRANSIT]: t("In transit"),
    [OrderTrackingShipmentStatus.OUT_FOR_DELIVERY]: t("Out for delivery"),
    [OrderTrackingShipmentStatus.DELIVERED]: t("Delivered"),
    [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP]: t(
      "Available for pickup",
    ),
    [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP_CARRIER]: t(
      "Available for pickup",
    ),
    [OrderTrackingShipmentStatus.STALLED_IN_TRANSIT]: t("Stalled in transit"),
    [OrderTrackingShipmentStatus.RETURN_TO_SENDER]: t("Return to sender"),
    [OrderTrackingShipmentStatus.DELIVERY_ATTEMPTED]: t("Delivery attempted"),
    [OrderTrackingShipmentStatus.DELIVERY_FAILURE]: t("Delivery failure"),
    [OrderTrackingShipmentStatus.SHIPMENT_CANCELLED]: t("Cancelled"),
    [OrderTrackingShipmentStatus.SHIPMENT_ERROR]: t("Error"),
    [OrderTrackingShipmentStatus.UNKNOWN]: t("Pre-transit"),
  };
}

// Built-in labels for the non-enum branches. Single source for both the
// resolveLabel fallbacks below and the inspector placeholders.
export function extraStatusStrings(): Record<
  TrackingDetailsExtraStatusKey,
  string
> {
  return {
    preparing: t("Your order is being prepared"),
    purchasedInStore: t("Purchased in store"),
    readyForPickup: t("Ready for pickup"),
    pickedUp: t("Picked up"),
    cancelledMessage: t(
      "Your order has been cancelled, please contact customer support for inquiries",
    ),
  };
}

// All renameable labels keyed by override key, in inspector display order.
export function allStatusLabelDefaults(): Record<
  TrackingDetailsStatusLabelKey,
  string
> {
  return { ...statusStrings(), ...extraStatusStrings() };
}

export function getShipmentMapStatusLabel(
  shipment: TrackingPageShipment | undefined,
  statusLabelOverrides?: StatusLabelOverrides,
): string | null {
  if (!shipment) {
    return null;
  }
  const fallback = statusStrings()[shipment.status] ?? t("Pre-transit");
  return resolveLabel(shipment.status, statusLabelOverrides, fallback);
}

function isStalledInTransit(
  shipment: TrackingPageShipment,
  stalledDays: number | null,
): boolean {
  if (stalledDays === null || stalledDays <= 0) {
    return false;
  }
  const updatedAt = shipment.updatedAt;
  if (!updatedAt) {
    return false;
  }
  const daysSinceUpdate =
    (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceUpdate >= stalledDays;
}

// Derive status copy locally so the block doesn't need a React i18n provider
// mounted; top-level `t` falls back to the source string when uninitialized.
export function getShipmentStatusText({
  shipment,
  cancelReason,
  source,
  pickupConfig,
  stalledDays,
  statusLabelOverrides,
}: {
  shipment: TrackingPageShipment | undefined;
  cancelReason?: string | null;
  source?: string | null;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
  statusLabelOverrides?: StatusLabelOverrides;
}): string {
  const extras = extraStatusStrings();
  if (cancelReason) {
    return resolveLabel(
      "cancelledMessage",
      statusLabelOverrides,
      extras.cancelledMessage,
    );
  }

  const { isPickUpOrder, readyForPickUp, hasBeenPickedUp } = pickupConfig;
  if (isPickUpOrder && readyForPickUp) {
    return resolveLabel(
      "readyForPickup",
      statusLabelOverrides,
      extras.readyForPickup,
    );
  }
  if (isPickUpOrder && hasBeenPickedUp) {
    return resolveLabel("pickedUp", statusLabelOverrides, extras.pickedUp);
  }

  if (!shipment) {
    if (source === "pos") {
      return resolveLabel(
        "purchasedInStore",
        statusLabelOverrides,
        extras.purchasedInStore,
      );
    }
    return resolveLabel("preparing", statusLabelOverrides, extras.preparing);
  }

  if (
    shipment.status === OrderTrackingShipmentStatus.STALLED_IN_TRANSIT ||
    (shipment.status === OrderTrackingShipmentStatus.IN_TRANSIT &&
      isStalledInTransit(shipment, stalledDays))
  ) {
    return resolveLabel(
      OrderTrackingShipmentStatus.STALLED_IN_TRANSIT,
      statusLabelOverrides,
      t("Stalled in transit"),
    );
  }

  const fallback = statusStrings()[shipment.status] ?? t("Pre-transit");
  return resolveLabel(shipment.status, statusLabelOverrides, fallback);
}

export const CarrierBrandIcon = memo(function CarrierBrandIcon({
  carrier,
}: {
  carrier: string | null;
}) {
  const Icon = getCarrierIcon(carrier);
  const label = carrier ? getCarrierDisplayName(carrier) : t("Carrier");
  return createElement(Icon, {
    "aria-label": t("Carrier: {{label}}", { label }),
    height: 24,
    style: { display: "block", flexShrink: 0 },
  });
});

export function formatEventTimestamp(
  timestamp: Date | undefined,
  locale?: string,
) {
  if (!timestamp) {
    return "";
  }
  try {
    const instant = timestamp.toTemporalInstant();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return instant.toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: tz,
    });
  } catch {
    return "";
  }
}

// Localized delivery-date formatters. The source dates are read in UTC
// (the block historically used getUTCDay/getUTCMonth), so we format in UTC
// too to keep the displayed day stable across the viewer's timezone.
export function formatDeliveryWeekdayMonth(
  date: Date,
  locale?: string,
): string {
  // formatToParts + explicit join keeps weekday-first order across all locales
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    timeZone: "UTC",
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  return [weekday, month].filter(Boolean).join(" ");
}

export function formatDeliveryDate(date: Date, locale?: string): string {
  // formatToParts + explicit join keeps weekday-first order across all locales
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  return [weekday, month, day].filter(Boolean).join(" ");
}

/**
 * Never hides a date, nor a delivered shipment — that header is a delivery
 * confirmation rather than an estimate, even when `deliveredAt` is missing.
 */
export function shouldHideDeliveryEstimateSection({
  hideWhenUnknown,
  date,
  isDelivered,
}: {
  hideWhenUnknown: boolean | undefined;
  date: Date | null | undefined;
  isDelivered: boolean;
}): boolean {
  return !!hideWhenUnknown && !date && !isDelivered;
}

export function isDelayedDelivery(
  shipment: TrackingPageShipment,
  today = Temporal.Now.plainDateISO("UTC"),
) {
  return !!(
    shipment.status !== OrderTrackingShipmentStatus.DELIVERED &&
    shipment.estimatedDeliveryAt &&
    Temporal.PlainDate.compare(
      today,
      shipment.estimatedDeliveryAt
        .toTemporalInstant()
        .toZonedDateTimeISO("UTC")
        .toPlainDate(),
    ) > 0
  );
}

export function formatEventMessage(
  event: TrackingPageEvent,
  textRules: readonly TrackingTextRule[] = [],
): string {
  const status = TRACKER_STATUS_TO_SHIPMENT_STATUS[event.status];
  const message = applyTrackingTextRules(
    event.message,
    "message",
    textRules,
  ).text;
  const statusLabel = status ? statusStrings()[status] : t("Unknown");
  const location = applyTrackingLocationRules(event.location, textRules).text;

  return [message.trim() ? message : statusLabel, location]
    .filter((text) => text.trim())
    .join(", ");
}
